// Shared FICA per-document review constants for the AGENT/REVIEWER UI. This
// MIRRORS artifacts/api-server/src/lib/ficaReview.ts — the server validates
// against the same keys/reasons and is the source of truth. The two lists MUST
// stay in sync. The profile photo is intentionally NOT a FICA document and is
// excluded from review.

export const ONBOARDING_DOCUMENT_KEYS = [
  "id_document",
  "proof_of_address",
  "bank_confirmation",
] as const;

export type OnboardingDocumentKey = (typeof ONBOARDING_DOCUMENT_KEYS)[number];

export const DOCUMENT_LABELS: Record<OnboardingDocumentKey, string> = {
  id_document: "ID Document",
  proof_of_address: "Proof of Address",
  bank_confirmation: "Bank Confirmation Letter",
};

// Maps a document key onto the path field carried by the onboarding/review
// payload, so the UI can tell whether a document was actually uploaded.
export const DOCUMENT_PATH_FIELDS: Record<
  OnboardingDocumentKey,
  "idDocumentPath" | "proofOfAddressPath" | "bankConfirmationPath"
> = {
  id_document: "idDocumentPath",
  proof_of_address: "proofOfAddressPath",
  bank_confirmation: "bankConfirmationPath",
};

export type RejectionReason = { value: string; label: string };

// Controlled reasons a reviewer must pick from when rejecting a document. Keep
// in sync with the server list. "other" is the escape hatch and relies on the
// free-text comment.
export const REJECTION_REASONS: readonly RejectionReason[] = [
  { value: "illegible", label: "Illegible or too blurry" },
  { value: "expired", label: "Document has expired" },
  { value: "name_mismatch", label: "Name does not match application" },
  { value: "wrong_document", label: "Wrong document type uploaded" },
  { value: "incomplete", label: "Incomplete or cut off" },
  { value: "outdated", label: "Older than 3 months" },
  { value: "details_mismatch", label: "Details do not match application" },
  { value: "other", label: "Other (see comment)" },
];

export function rejectionReasonLabel(
  value: string | null | undefined,
): string {
  if (!value) return "";
  return REJECTION_REASONS.find((r) => r.value === value)?.label ?? value;
}
